// deps
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

// internal-deps
import { GenericError, RETRY, captureError } from "@swiggy-private/core-ui";
import {
    getBridgeSdk,
    getUserInfo,
    UserContext,
    recordGenericErrorNREvent,
    recordNREvent,
    NEWRELIC_TABLES,
} from "@swiggy-private/checkout-core";
import { CART_GENERIC_EVENT_CASES } from "@swiggy-private/cart-ui";

// interfaces
import { CtaAction } from "@cart-client/interfaces/Cart";

// contexts
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartContext } from "@cart-client/contexts/CartContext";

// helpers/utils
import { syncClientHeaders } from "@cart-client/helpers/Header";
import { getStaticMwebCartPath } from "@cart-client/helpers/Paths";
import { setCartFromLoginFlowFlag } from "@cart-client/helpers/Login";

interface CartErrorProps {
    titleMessage?: string;
    statusMessage?: string;
    stayOnCartScreen?: boolean;
    ctaAction?: CtaAction;
    ctaText?: string;
    goToHome: VoidFunction;
    goBack: VoidFunction;
    imageId?: string;
    isPrimaryAction?: boolean;
}

const CartError: React.FC<CartErrorProps> = ({
    titleMessage,
    statusMessage,
    stayOnCartScreen,
    ctaAction,
    ctaText,
    goToHome,
    goBack,
    imageId,
    isPrimaryAction,
}: CartErrorProps) => {
    const { doUpdateCart, updateCartInProgress, reloadCart } = useContext(UpdateCartContext);
    const { setUser } = useContext(UserContext);
    const [updating, setUpdating] = useState<boolean>(false);
    const {
        cartState: { localCart },
    } = useContext(CartContext);

    useEffect(() => {
        if (!updateCartInProgress && updating && !stayOnCartScreen) {
            setUpdating(false);
            goBack();
        }
    }, [goBack, updateCartInProgress, stayOnCartScreen, updating]);

    const onImpression = useCallback(
        () => recordGenericErrorNREvent({ errorContext: CART_GENERIC_EVENT_CASES.GENERIC_CART_ERROR }),
        [],
    );
    const goToLogin = useCallback(async (): Promise<boolean> => {
        try {
            setCartFromLoginFlowFlag();
            
            return getBridgeSdk()
                .login({ prevPathWeb: getStaticMwebCartPath() })
                .then(async (response) => {
                    if (!response || !response.hasLoggedIn) {
                        return false;
                    }

                    recordNREvent({
                        eventType: NEWRELIC_TABLES.POST_LOGIN_FLOW,
                        source: "cart-error",
                    });
                    await syncClientHeaders();
                    // update User Context
                    try {
                        const userInfo = await getUserInfo();
                        if (userInfo.isSome()) {
                            setUser(userInfo.unwrap());
                        }
                    } catch (error) {
                        captureError(error);
                    }
                    reloadCart();
                    return true;
                });
        } catch (err) {
            captureError(err);
            return false;
        }
    }, [reloadCart, setUser]);

    const onClick = useCallback(async () => {
        if (ctaAction === CtaAction.CLEAR_CART) {
            getBridgeSdk()
                .clearLocalCart({
                    tenant: { marketplaceBusinessline: "Regular", marketplaceCategory: "Food", marketplaceId: 1 },
                })
                .catch(() => void 0);
            await doUpdateCart({ ...localCart, cartItems: [], mealItems: [] });
            setUpdating(true);
            return;
        }
        if (ctaAction === CtaAction.GO_TO_HOME) {
            goToHome();
        }

        if (ctaAction === CtaAction.GO_TO_LOGIN) {
            goToLogin();
        }

        if (!stayOnCartScreen) {
            goBack();
        }
    }, [ctaAction, stayOnCartScreen, doUpdateCart, localCart, goToHome, goToLogin, goBack]);

    const primaryAction = useMemo(() => {
        if (isPrimaryAction) {
            return { onClick, text: ctaText || RETRY };
        }
        return null;
    }, [isPrimaryAction, onClick, ctaText]);

    const action = useMemo(() => {
        if (!isPrimaryAction) {
            return { onClick, text: ctaText };
        }
        return null;
    }, [isPrimaryAction, onClick, ctaText]);

    return (
        <GenericError
            pageImpressionEvent={onImpression}
            primaryAction={primaryAction}
            action={action}
            title={titleMessage}
            message={statusMessage}
            imageId={imageId}
        />
    );
};

export default React.memo(CartError);
